<template >
  <v-container>
    <v-col style="padding: 0;">
      <v-row>
        <h4>{{ title }} <span v-if="required" class="red--text">*</span></h4>
      </v-row>
      <v-row v-if="validationError">
        <span class="red--text text-caption">{{ validationError }}</span>
      </v-row>
    </v-col>

    <v-row>
      <v-col>
        <v-hover v-slot="{ hover }" >
          <v-card :class="{ 'on-hover': hover }" class="d-flex justify-center align-center" outlined max-width="250"
            style="height: 200px; cursor: pointer; overflow: hidden;" @click="!disabled && addFile()">
            <v-overlay absolute style="opacity: 0.8" :value="loading">
              <v-progress-circular indeterminate color="primary" :size="50"></v-progress-circular>
            </v-overlay>
            <template v-if="isImage">
              <v-img :src="filePreview" alt="Pré-visualização da imagem" contain class="image-preview"></v-img>
            </template>
            <template v-else-if="isPdf">
              <iframe
                :src="fileUrl"
                frameborder="0"
                style="width: 100%; height: 100%;"
                title="Pré-visualização do PDF"
              ></iframe>
            </template>
            <template v-else-if="isExcel">
              <div class="d-flex flex-column align-center">
                <v-icon size="64" color="green">mdi-file-excel</v-icon>
                <span class="mt-2 text-caption text-center px-2" style="word-break: break-word;">{{ fileName }}</span>
              </div>
            </template>
            <template v-else>
              <v-icon size="48">mdi-plus</v-icon>
            </template>

            <template v-if="hover && (isPdf || isImage || isExcel)">
              <v-overlay absolute opacity="0.8" color="black" :style="isPdf? 'width: 93%;' : ''">
                <div class="d-flex flex-column align-center">
                  <v-btn color="primary" class="mb-2" >
                    <a 
                      v-if="isTablet"
                      :href="fileUrl" 
                      :download="fileName"
                    >
                      <v-icon color="white">mdi-download</v-icon>
                    </a>
                    <a 
                      v-else
                      :href="fileUrl" 
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <v-icon color="white">mdi-download</v-icon>
                    </a>
                  </v-btn>
                  <v-btn :disabled="disabled"  color="error" @click.stop="confirmDelete()">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </div>
              </v-overlay>
            </template>

          </v-card>

        </v-hover>
      </v-col>
    </v-row>

    <!-- Input de arquivo oculto -->
    <input :disabled="disabled" type="file" :ref="refInput" style="display: none;" :accept="computedAccept" @change="handleFileUpload" />
    <samp style="font-size: 12px;"> {{ fileName }}</samp>
    <!-- Botão para limpar arquivo -->

  </v-container>
</template>

<script>
import { showDeleteDialogAlert } from "@/utils/DialogAlertHelper";
import compressor from "@/utils/CompressorFile.js"

export default {
  props: {
    refInput: {
      type: Number,
      default: 1111,
    },
    compressorConfig: {
      type: Object,
      default: () => ({
        quality: 0.4,
        maxWidth: 800,
        maxHeight: 800,
        convertSize: 0,
        mimeType: "image/webp",
      }),
    },
    title: {
      type: String,
      default: "Adicione um arquivo", // Título padrão
    },
    loading: {
      type: Boolean,
      default: false, // Título padrão
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showFile: {
      type: [Object, Blob], // Aceita tanto Object quanto Blob
      default: () => ({}), // Valor padrão
    },
    fileTypes: {
      type: Array,
      default: () => [],
    },
    required: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      file: null,
      filePreview: null,
      fileUrl: null
    };
  },
  computed: {
    computedAccept() {
      const accept = [];
      if (this.fileTypes.includes("image")) accept.push("image/*");
      if (this.fileTypes.includes("pdf")) accept.push(".pdf");
      if (this.fileTypes.includes("xlsx")) accept.push(".xlsx");
      if (this.fileTypes.includes("xls")) accept.push(".xls");
      return accept.join(",");
    },
    isImage() {
      return this.file && this.file.type.startsWith("image/");
    },
    fileName() {
      return this.file && this.file.name ? this.file.name : "";
    },
    isPdf() {
      return this.file && this.file.type === "application/pdf";
    },
    isExcel() {
      return this.file && (
        this.file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" || 
        this.file.type === "application/vnd.ms-excel" ||
        this.file.name?.endsWith('.xlsx') ||
        this.file.name?.endsWith('.xls')
      );
    },
    validationError() {
      if (!this.rules || !this.rules.length) return null;
      for (const rule of this.rules) {
        const result = rule(this.file);
        if (typeof result === 'string') return result;
      }
      return null;
    },
    isTablet() {
      // Detecta se é um tablet baseado em touch points, tamanho da tela e user agent
      const hasTouch = navigator.maxTouchPoints > 0 || 'ontouchstart' in window;
      const width = window.innerWidth;
      const height = window.innerHeight;
      const isTabletSize = (width >= 600 && width <= 1024) || (height >= 600 && height <= 1024);
      
      // Verifica user agent para tablets comuns
      const userAgent = navigator.userAgent.toLowerCase();
      const isTabletUA = /tablet|ipad|playbook|silk|android(?!.*mobile)/i.test(userAgent);
      
      // É tablet se tiver touch, tamanho de tablet e não for mobile pequeno
      return hasTouch && isTabletSize && (isTabletUA || width >= 768);
    },
  },
  methods: {
    // Função centralizada para limpar URLs de objeto
    clearObjectURLs() {
      if (this.fileUrl) {
        URL.revokeObjectURL(this.fileUrl)
      }
      if (this.filePreview) {
        URL.revokeObjectURL(this.filePreview)
      }
    },
    triggerFileInput() {
      this.$refs[this.refInput].click();
    },
    async handleFileUpload(event) {
      // Limpar URLs antigas antes de processar o novo arquivo
      this.clearObjectURLs()
      
      this.file = event.target.files[0];
      if (!this.file) return;
      
      if (this.isImage) {
        this.file = await compressor.compressorFile(this.file,this.compressorConfig);
        this.filePreview = URL.createObjectURL(this.file);
      } else if (this.isPdf) {
        this.filePreview = null;
      } else if (this.isExcel) {
        // Excel não precisa de preview, apenas validar que é um arquivo válido
        this.filePreview = null;
      } else {
        alert("Tipo de arquivo inválido.");
        this.clearFile();
        return;
      }
      
      // Criar URL apenas se tiver um arquivo válido
      if (this.file instanceof Blob || this.file instanceof File) {
        this.fileUrl = URL.createObjectURL(this.file);
      }
      
      this.$emit("change:file", this.file)
    },
    confirmDelete() {
      if (!this.file) return;
      const message = this.fileName 
        ? `Deseja excluir o arquivo '${this.fileName}'?` 
        : 'Deseja excluir este arquivo?';
      showDeleteDialogAlert(message, this.clearFile);
    },
    clearFile() {
      // Limpar URLs de objeto antes de limpar as referências
      this.clearObjectURLs()
      
      this.file = null;
      this.fileUrl = null;
      this.filePreview = null;
      if (this.$refs[this.refInput]) this.$refs[this.refInput].value = null;
      this.$emit("change:file", this.file)
    },
    addFile() {
      if (!this.fileUrl) this.triggerFileInput();
    },
    openFile() {
      if (this.fileUrl) window.open(this.fileUrl, "_blank")
    },
    async urlToFile(url, fileName) {
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const file = new File([blob], fileName, { type: blob.type });
        return file;
      } catch (error) {
        console.error("Erro ao converter URL para File:", error);
        throw error;
      }
    }
  },
  watch:{
    showFile:{
      immediate: true, 
      handler(newValue){
        // Limpar URLs antigas antes de processar o novo valor
        this.clearObjectURLs()
        
        if (!newValue || !(newValue instanceof Blob || newValue instanceof File)) {
          this.clearFile();
          return;
        }
        
        this.file = newValue;
        
        // Criar URL apenas se for um Blob ou File válido
        if (this.file instanceof Blob || this.file instanceof File) {
          this.fileUrl = URL.createObjectURL(newValue);
        }

        if (this.isImage) {
          this.filePreview = URL.createObjectURL(newValue);
        } else if (this.isPdf) {
          this.filePreview = null;
        } else if (this.isExcel) {
          // Excel não precisa de preview
          this.filePreview = null;
        } else {
          // Se não for nenhum tipo válido, limpar
          this.clearFile();
        }
        this.$emit("change:file", this.file)
      }
    }
  },  
  created(){
  },
  beforeDestroy() {
    // Limpar URLs de objeto para evitar vazamentos de memória
    this.clearObjectURLs()
  }
};
</script>

<style scoped>
.image-preview {
  object-fit: contain;
  max-width: 100%;
  max-height: 100%;
  height: auto;
  width: auto;
}
</style>